// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect } from 'react';
import { Box, useTheme } from '@mui/system';
import { CircularProgress, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { WideLightTooltip } from 'Components/Tooltip';
import { formatValue } from 'Utils/deliverables';
import { formatPromotionBound, formatUtcInstant, promotionStatus } from 'Utils/promotion';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { GetPromotionsThunk } from '../adminThunk';
import stylesDef from './styles';

const typeLabels: Record<string, string> = { percent_off: 'Percent off' };

const eligibilityLabel = (eligibility: IAdminPromotion['eligibility']): string => {
  if (eligibility.scope === 'payg') return 'PAYG';
  if (eligibility.scope === 'orgs') return `Orgs (${eligibility.orgIds.length})`;
  return eligibility.scope.charAt(0).toUpperCase() + eligibility.scope.slice(1);
};

// Read-only: scripts/insert_promotion.js is the write path (AUT-6455)
export const Promotions = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { promotions, promotionsLoading } = useAppSelector((state) => state.admin);
  const now = new Date();

  useEffect(() => {
    dispatch(GetPromotionsThunk());
  }, []);

  return (
    <Box sx={styles.pageWrapper}>
      <Box sx={styles.header}>
        <Typography variant="h2">Promotions</Typography>
      </Box>
      <Divider sx={styles.divider} />
      {promotionsLoading && (
        <Box sx={styles.spinnerWrapper}>
          <CircularProgress size={50} />
        </Box>
      )}
      {!promotionsLoading && promotions.length === 0 && (
        <Typography sx={styles.emptyState}>No promotions yet — create one with scripts/insert_promotion.js</Typography>
      )}
      {!promotionsLoading && promotions.length > 0 && (
        <Table aria-label="promotions table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.headCell}>Name</TableCell>
              <TableCell sx={styles.headCell}>Display text</TableCell>
              <TableCell sx={styles.headCell}>Type</TableCell>
              <TableCell sx={styles.headCell} align="right">Value</TableCell>
              <TableCell sx={styles.headCell}>Window</TableCell>
              <TableCell sx={styles.headCell}>Eligibility</TableCell>
              <TableCell sx={styles.headCell}>Status</TableCell>
              <TableCell sx={styles.headCell} align="right">Orders</TableCell>
              <TableCell sx={styles.headCell} align="right">Discount given</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promotions.map((promotion) => (
              <TableRow key={promotion._id}>
                <TableCell sx={styles.cellText} component="th" scope="row">{promotion.name}</TableCell>
                <TableCell sx={styles.cellText}>{promotion.displayText}</TableCell>
                <TableCell sx={styles.cellText}>{typeLabels[promotion.type] ?? promotion.type}</TableCell>
                <TableCell sx={styles.cellText} align="right">{`${promotion.valuePercent}%`}</TableCell>
                <TableCell sx={styles.cellText}>
                  <WideLightTooltip title={`${formatUtcInstant(promotion.startDate)} to ${formatUtcInstant(promotion.endDate)}`}>
                    <Box component="span" sx={styles.window}>
                      <span>{formatPromotionBound(promotion.startDate, promotion.startTimezone)}</span>
                      <span>{`to ${formatPromotionBound(promotion.endDate, promotion.endTimezone)}`}</span>
                    </Box>
                  </WideLightTooltip>
                </TableCell>
                <TableCell sx={styles.cellText}>{eligibilityLabel(promotion.eligibility)}</TableCell>
                <TableCell sx={styles.cellText}>{promotionStatus(promotion, now)}</TableCell>
                <TableCell sx={styles.cellText} align="right">{promotion.usage.orderCount}</TableCell>
                <TableCell sx={styles.cellText} align="right">{`$${formatValue(promotion.usage.dollarsGiven)}`}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={styles.end} />
    </Box>
  );
};

export default Promotions;
